export type SenderKind = "account" | "guest" | "bot";
export type PresenceKind = "human" | "guest" | "bot";
export type UserStatus = "active" | "suspended" | "banned" | "deleted" | "hidden";
export type UserRole = "user" | "admin" | "bot";
export type UserKind = "human" | "bot";
export type ReplyMode = "addressed_or_random" | "always_addressed" | "random_lurk" | "dm_only";

export type PresenceUser = {
  id: string;
  kind: PresenceKind;
  displayName: string;
  username?: string;
  avatarUrl: string;
  role?: UserRole;
};

export type ChatMessage = {
  id: string;
  roomId: string;
  senderId: string;
  senderKind: SenderKind;
  displayName: string;
  avatarUrl: string;
  body: string;
  imageKey?: string | null;
  imageUrl?: string | null;
  createdAt: string;
  deletedAt?: string | null;
  clientMsgId?: string;
};

export type ClientFrame =
  | { type: "message"; clientMsgId: string; body: string; imageKey?: string }
  | { type: "typing"; isTyping: boolean }
  | { type: "presence_ping" };

export type ServerFrame =
  | { type: "hello"; roomId: string; you: PresenceUser; history: ChatMessage[] }
  | { type: "message"; message: ChatMessage }
  | { type: "presence"; users: PresenceUser[] }
  | { type: "typing"; userId: string; displayName: string; isTyping: boolean }
  | { type: "error"; code: string; message: string }
  | { type: "moderation"; action: string; reason?: string }
  | { type: "system"; body: string; createdAt: string };

export type SessionUser = {
  id: string;
  kind: UserKind;
  email: string | null;
  username: string;
  displayName: string;
  avatarUrl: string;
  bio: string;
  status: UserStatus;
  role: UserRole;
  directoryVisible: boolean;
};

export type GuestIdentity = {
  guestId: string;
  displayName: string;
  avatarId: string;
  iat: number;
  exp: number;
};

export type PublicSettings = {
  appName: string;
  tagline: string;
  logoUrl: string | null;
  theme: Record<string, string>;
  guestEnabled: boolean;
  maxMessageLength: number;
  welcomePublic: string;
  welcomeMembers: string;
};

export const RESERVED_USERNAMES = ["admin", "system", "support", "bot", "owner"] as const;

export const DEFAULT_AVATARS = Array.from({ length: 16 }, (_, i) =>
  String(i + 1).padStart(2, "0"),
);

export const ROOM_PUBLIC = "room:public";
export const ROOM_MEMBERS = "room:members";

export function dmRoomId(a: string, b: string): string {
  return "dm:" + [a, b].sort().join(":");
}

export function isDmRoom(roomId: string): boolean {
  return roomId.startsWith("dm:");
}

export function publicRoomPath(roomId: string): string {
  if (roomId === ROOM_PUBLIC) return "/room/public";
  if (roomId === ROOM_MEMBERS) return "/room/members";
  if (roomId.startsWith("dm:")) return `/dm/${roomId.slice(3)}`;
  return "/";
}

export const MAX_MESSAGE_CHARS = 2000;
export const HISTORY_DEFAULT = 200;
export const HISTORY_MAX = 500;
